import { TranslationsObject } from "@genericTypes/sharedTypes";
import { GQLAttachments, GQLTags, GQLUrls } from "@gql/types/gql";
import { translate } from "@helpers/translationObjectFormatter";
import {
    resolveNewsTagsColor,
    resolveURL,
    resolveNewsTagName,
    formatTimestamp,
    getDateLabelInfo,
} from "@lib/sharedUtils";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Badge from "../badge/Badge";
import style from "./style.module.scss";
import { titleDecoder } from "@lib/sharedUtils";
export interface INewsCardProps {
    url: GQLUrls;
    title: string;
    image: GQLAttachments;
    tags: GQLTags[];
    createdAt?: string;
    updatedAt?: string;
    hideTag?: boolean;
}

const NewsCard = ({
    url,
    title,
    image,
    tags,
    createdAt,
    updatedAt,
    hideTag,
}: INewsCardProps) => {
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const dateLabelInfo = getDateLabelInfo(createdAt, updatedAt);
    const imageRef = useRef<HTMLDivElement>(null);
    const [imageInView, setImageInView] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setImageInView(true);
                observer.disconnect();
            }
        });
        if (imageRef.current) observer.observe(imageRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <a
            href={resolveURL(url)}
            className="flex flex-row sm:flex-col gap-4 max-w-[380px] sm:max-w-[300px] lg:max-w-[380px] hover:no-underline"
            key={title}
        >
            <div
                ref={imageRef}
                className="w-[150px] h-[100px] sm:w-[300px] sm:h-[200px] lg:w-[380px] lg:h-[250px] shrink-0 mb-5"
            >
                {imageInView && (
                    <Image
                        className="max-w-[150px] sm:max-w-[300px] lg:max-w-[380px] sm:min-h-[200px] sm:max-h-[200px] lg:min-h-[250px] lg:max-h-[250px]"
                        src={image?.fullPath as string}
                        alt={title}
                        width={380}
                        height={250}
                        sizes="(max-width: 640px) 150px, (max-width: 1024px) 300px, 380px"
                    />
                )}
                {dateLabelInfo.date && (
                    <span
                        className="text-sm mt-2 hidden lg:block"
                        style={{ color: "#4C4C4C" }}
                    >
                        {formatTimestamp(dateLabelInfo.date)}
                    </span>
                )}
            </div>
            <div className="flex flex-col gap-1 sm:gap-3">
                {dateLabelInfo.date && (
                    <span
                        className="block text-xs lg:hidden"
                        style={{ color: "#4C4C4C" }}
                    >
                        {formatTimestamp(dateLabelInfo.date)}
                    </span>
                )}
                {!hideTag && tags && tags.length > 0 && (
                    <Badge
                        text={
                            resolveNewsTagName(
                                tags as { name: string }[],
                            ) as string
                        }
                        variant={resolveNewsTagsColor(
                            resolveNewsTagName(
                                tags as { name: string }[],
                            ) as string,
                        )}
                        className="capitalize text-xs sm:text-base"
                    />
                )}
                <span
                    className={`${style.blogTitle} text-xs sm:text-lg font-bold text-black`}
                >
                    {titleDecoder(title ?? "")}
                </span>
                <span className="text-xs sm:text-base">
                    {translate(translations, "several_general_readMoreButton")}
                </span>
            </div>
        </a>
    );
};

export default NewsCard;
